/**
 * Supplier model translations
 *
 * Translation keys follow the pattern: models.supplier.{category}.{key}
 * Categories: fields, tabs, blocks, badges, actions, filters, options
 *
 * Note: vue-i18n resolves dots as nested paths, so nested field keys
 * like "supplier_data.category" must be structured as nested objects.
 */
export default {
  en: {
    // Model names
    singular: "Supplier",
    plural: "Suppliers",

    // Tabs
    tabs: {
      general: "General",
      supplier: "Supplier Details",
      ordering: "Ordering",
      compliance: "Compliance"
    },

    // Blocks
    blocks: {
      identity: "Identity",
      contact: "Contact",
      classification: "Classification",
      financial: "Financial",
      ordering: "Ordering",
      transaction_settings: "Transaction Settings",
      compliance: "Compliance",
      supplier_info: "Supplier Information"
    },

    // Fields - nested structure for dot-notation keys
    fields: {
      // Identity fields
      code: "Code",
      name: "Name",
      legal_name: "Legal Name",
      type: "Type",
      status: "Status",
      label: "Label",
      website: "Website",
      notes: "Notes",
      created_at: "Created At",
      updated_at: "Updated At",

      // Supplier-specific data (JSONB)
      supplier_data: {
        category: "Category",
        supplier_status: "Supplier Status",
        is_preferred: "Preferred Supplier",
        rating: "Rating",
        tax_id: "Tax ID",
        is_1099_required: "1099 Required",
        payment_terms_days: "Payment Terms (Days)",
        payment_method: "Payment Method",
        currency: "Currency",
        minimum_order_cents: "Minimum Order",
        lead_time_days: "Lead Time (Days)",
        procurement_contact_id: "Procurement Contact",
        approved_at: "Approved At",
        approved_by: "Approved By",
        last_order_at: "Last Order At",
        suspended_at: "Suspended At",
        suspension_reason: "Suspension Reason",
        reactivation_notes: "Reactivation Notes",
        bank_name: "Bank Name",
        bank_account_last_four: "Bank Account (Last 4)",
        bank_routing_last_four: "Routing Number (Last 4)",
        po_email: "PO Email",
        accepts_electronic_po: "Accepts Electronic PO",
        requires_po_number: "Requires PO Number",
        insurance_expiry: "Insurance Expiry",
        w9_on_file: "W-9 On File",
        certifications: "Certifications"
      },

      // Transaction settings (JSONB)
      transaction_settings: {
        currency: "Currency",
        language: "Language",
        default_discount: "Default Discount"
      }
    },

    // Badges (for badge columns and select displays)
    badges: {
      // Status
      active: "Active",
      inactive: "Inactive",

      // Supplier Status
      pending: "Pending Approval",
      approved: "Approved",
      suspended: "Suspended",

      // Category
      parts: "Parts & Components",
      services: "Professional Services",
      equipment: "Equipment & Machinery",
      consumables: "Consumables & Supplies",
      raw_materials: "Raw Materials",
      logistics: "Logistics & Shipping",
      other: "Other",

      // Payment Method
      check: "Check",
      ach: "ACH Transfer",
      wire: "Wire Transfer",
      credit_card: "Credit Card",

      // Type
      company: "Company",
      individual: "Individual",
      government: "Government",
      nonprofit: "Non-Profit",

      // Boolean
      true: "Yes",
      false: "No"
    },

    // Filters
    filters: {
      status: "Status",
      "supplier_data->category": "Category",
      "supplier_data->supplier_status": "Supplier Status",
      "supplier_data->is_preferred": "Preferred",
      type: "Type"
    },

    // Options (for select/toggleGroup fields)
    options: {
      type: {
        company: "Company",
        individual: "Individual",
        government: "Government",
        nonprofit: "Non-Profit",
        other: "Other"
      },
      supplier_data: {
        category: {
          parts: "Parts & Components",
          services: "Professional Services",
          equipment: "Equipment & Machinery",
          consumables: "Consumables & Supplies",
          raw_materials: "Raw Materials",
          logistics: "Logistics & Shipping",
          other: "Other"
        },
        supplier_status: {
          pending: "Pending Approval",
          approved: "Approved",
          active: "Active",
          suspended: "Suspended",
          inactive: "Inactive"
        },
        payment_method: {
          check: "Check",
          ach: "ACH Transfer",
          wire: "Wire Transfer",
          credit_card: "Credit Card"
        },
        is_preferred: {
          true: "Yes",
          false: "No"
        }
      }
    }
  },

  fr: {
    // Model names
    singular: "Fournisseur",
    plural: "Fournisseurs",

    // Tabs
    tabs: {
      general: "Général",
      supplier: "Détails du fournisseur",
      ordering: "Commandes",
      compliance: "Conformité"
    },

    // Blocks
    blocks: {
      identity: "Identité",
      contact: "Contact",
      classification: "Classification",
      financial: "Finances",
      ordering: "Commandes",
      transaction_settings: "Paramètres de transaction",
      compliance: "Conformité",
      supplier_info: "Informations fournisseur"
    },

    // Fields - nested structure for dot-notation keys
    fields: {
      // Identity fields
      code: "Code",
      name: "Nom",
      legal_name: "Raison sociale",
      type: "Type",
      status: "Statut",
      label: "Étiquette",
      website: "Site web",
      notes: "Notes",
      created_at: "Créé le",
      updated_at: "Modifié le",

      // Supplier-specific data (JSONB)
      supplier_data: {
        category: "Catégorie",
        supplier_status: "Statut fournisseur",
        is_preferred: "Fournisseur privilégié",
        rating: "Évaluation",
        tax_id: "Numéro de taxe",
        is_1099_required: "1099 requis",
        payment_terms_days: "Délai de paiement (jours)",
        payment_method: "Mode de paiement",
        currency: "Devise",
        minimum_order_cents: "Commande minimale",
        lead_time_days: "Délai de livraison (jours)",
        procurement_contact_id: "Contact approvisionnement",
        approved_at: "Approuvé le",
        approved_by: "Approuvé par",
        last_order_at: "Dernière commande",
        suspended_at: "Suspendu le",
        suspension_reason: "Raison de suspension",
        reactivation_notes: "Notes de réactivation",
        bank_name: "Nom de la banque",
        bank_account_last_four: "Compte bancaire (4 derniers)",
        bank_routing_last_four: "Transit (4 derniers)",
        po_email: "Courriel pour bons de commande",
        accepts_electronic_po: "Accepte les BC électroniques",
        requires_po_number: "Numéro de BC requis",
        insurance_expiry: "Expiration de l'assurance",
        w9_on_file: "Formulaire W-9 au dossier",
        certifications: "Certifications"
      },

      // Transaction settings (JSONB)
      transaction_settings: {
        currency: "Devise",
        language: "Langue",
        default_discount: "Remise par défaut"
      }
    },

    // Badges
    badges: {
      // Status
      active: "Actif",
      inactive: "Inactif",

      // Supplier Status
      pending: "En attente d'approbation",
      approved: "Approuvé",
      suspended: "Suspendu",

      // Category
      parts: "Pièces et composants",
      services: "Services professionnels",
      equipment: "Équipements et machinerie",
      consumables: "Consommables et fournitures",
      raw_materials: "Matières premières",
      logistics: "Logistique et expédition",
      other: "Autre",

      // Payment Method
      check: "Chèque",
      ach: "Virement ACH",
      wire: "Virement bancaire",
      credit_card: "Carte de crédit",

      // Type
      company: "Entreprise",
      individual: "Particulier",
      government: "Gouvernement",
      nonprofit: "Organisme à but non lucratif",

      // Boolean
      true: "Oui",
      false: "Non"
    },

    // Filters
    filters: {
      status: "Statut",
      "supplier_data->category": "Catégorie",
      "supplier_data->supplier_status": "Statut fournisseur",
      "supplier_data->is_preferred": "Privilégié",
      type: "Type"
    },

    // Options
    options: {
      type: {
        company: "Entreprise",
        individual: "Particulier",
        government: "Gouvernement",
        nonprofit: "Organisme à but non lucratif",
        other: "Autre"
      },
      supplier_data: {
        category: {
          parts: "Pièces et composants",
          services: "Services professionnels",
          equipment: "Équipements et machinerie",
          consumables: "Consommables et fournitures",
          raw_materials: "Matières premières",
          logistics: "Logistique et expédition",
          other: "Autre"
        },
        supplier_status: {
          pending: "En attente d'approbation",
          approved: "Approuvé",
          active: "Actif",
          suspended: "Suspendu",
          inactive: "Inactif"
        },
        payment_method: {
          check: "Chèque",
          ach: "Virement ACH",
          wire: "Virement bancaire",
          credit_card: "Carte de crédit"
        },
        is_preferred: {
          true: "Oui",
          false: "Non"
        }
      }
    }
  }
};
